import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  useTheme,
} from '@mui/material';

const DPI = 96; // web default

const PRESETS = [
  { id: 'custom', name: 'Custom', wIn: null, hIn: null },
  { id: 'letter', name: 'Letter (8.5 x 11 in)', wIn: 8.5, hIn: 11 },
  { id: 'legal', name: 'Legal (8.5 x 14 in)', wIn: 8.5, hIn: 14 },
  { id: 'tabloid', name: 'Tabloid (11 x 17 in)', wIn: 11, hIn: 17 },
  { id: 'a4', name: 'A4 (210 x 297 mm)', wIn: 8.27, hIn: 11.69 },
  { id: 'square1080', name: 'Square 1080 px', wPx: 1080, hPx: 1080 },
  { id: 'square2048', name: 'Square 2048 px', wPx: 2048, hPx: 2048 },
];

function toPx(val, units) {
  if (units === 'px') return val;
  return Math.round(val * DPI);
}
function toIn(val, units) {
  if (units === 'in') return val;
  return +(val / DPI).toFixed(2);
}

const PageSettingsDialog = ({ open, initial = { widthPx: 1080, heightPx: 1080 }, onClose, onApply }) => {
  const theme = useTheme();
  const [units, setUnits] = React.useState('px'); // 'px' | 'in'
  const [orientation, setOrientation] = React.useState('portrait'); // 'portrait' | 'landscape'
  const [preset, setPreset] = React.useState('custom');
  const [width, setWidth] = React.useState(1080);
  const [height, setHeight] = React.useState(1080);

  React.useEffect(() => {
    if (!open) return;
    // seed from initial
    const w = initial?.widthPx ?? 1080;
    const h = initial?.heightPx ?? 1080;
    setUnits('px');
    setOrientation(w >= h ? 'landscape' : 'portrait');
    setPreset('custom');
    setWidth(w);
    setHeight(h);
  }, [open, initial]);

  const handleUnitsChange = (_e, val) => {
    if (!val) return;
    if (val === units) return;
    // Convert existing values when switching units
    if (val === 'in') {
      setWidth(toIn(width, 'px'));
      setHeight(toIn(height, 'px'));
    } else {
      setWidth(toPx(width, 'in'));
      setHeight(toPx(height, 'in'));
    }
    setUnits(val);
  };

  const applyPreset = (id) => {
    setPreset(id);
    const p = PRESETS.find((x) => x.id === id);
    if (!p || id === 'custom') return;
    let wpx = p.wPx ?? (p.wIn ? Math.round(p.wIn * DPI) : undefined);
    let hpx = p.hPx ?? (p.hIn ? Math.round(p.hIn * DPI) : undefined);
    if (orientation === 'landscape') {
      const tmp = wpx; wpx = hpx; hpx = tmp;
    }
    if (units === 'px') {
      setWidth(wpx);
      setHeight(hpx);
    } else {
      setWidth(+(wpx / DPI).toFixed(2));
      setHeight(+(hpx / DPI).toFixed(2));
    }
  };

  const onOrientationChange = (_e, val) => {
    if (!val) return;
    if (val !== orientation) {
      // swap width/height when switching orientation
      setOrientation(val);
      const w = width; const h = height;
      setWidth(h);
      setHeight(w);
    }
  };

  const handleApply = () => {
    const wPx = units === 'px' ? Number(width) : Math.round(Number(width) * DPI);
    const hPx = units === 'px' ? Number(height) : Math.round(Number(height) * DPI);
    if (Number.isFinite(wPx) && Number.isFinite(hPx) && wPx > 0 && hPx > 0) {
      onApply?.({ widthPx: wPx, heightPx: hPx });
    } else {
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Page Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Orientation</Typography>
            <ToggleButtonGroup size="small" exclusive value={orientation} onChange={onOrientationChange} sx={{ ml: 1 }}>
              <ToggleButton value="portrait">Portrait</ToggleButton>
              <ToggleButton value="landscape">Landscape</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TextField
            select
            size="small"
            label="Preset"
            value={preset}
            onChange={(e) => applyPreset(e.target.value)}
          >
            {PRESETS.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Units</Typography>
            <ToggleButtonGroup size="small" exclusive value={units} onChange={handleUnitsChange} sx={{ ml: 1 }}>
              <ToggleButton value="px">Pixels</ToggleButton>
              <ToggleButton value="in">Inches</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              type="number"
              size="small"
              label={`Width (${units})`}
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              inputProps={{ min: 1, step: units === 'px' ? 1 : 0.01 }}
              fullWidth
            />
            <TextField
              type="number"
              size="small"
              label={`Height (${units})`}
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              inputProps={{ min: 1, step: units === 'px' ? 1 : 0.01 }}
              fullWidth
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleApply}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PageSettingsDialog;
